import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type { SupportTicketDto } from "../types";

/** Wire types for `/v1/hq/support/desk/*` (backend `support/desk.ts`). */

export interface DeskPerson {
  id: string;
  display_name: string | null;
  handle: string | null;
  avatar: string | null;
  email: string | null;
  account_status: string | null;
}

export interface DeskTicket extends SupportTicketDto {
  ref: string;
  created_at: string | null;
  tags: string[];
  first_response_at: string | null;
  merged_into_ticket_id: string | null;
  csat_rating: number | null;
  csat_comment: string | null;
  csat_submitted_at: string | null;
  sla_state: "met" | "ok" | "due_soon" | "breached" | "none";
  merged_into: { id: string; public_id: string } | null;
  merged_from: Array<{ id: string; public_id: string }>;
}

export interface ThreadMessage {
  id: string;
  message_type: "customer_message" | "agent_message" | "system_message" | "internal_reference";
  body_reference: string;
  sent_at: string;
  delivery_status: string;
  author: DeskPerson | null;
  from_customer: boolean;
}

export interface TicketView {
  ticket: DeskTicket;
  requester: DeskPerson | null;
  subject: DeskPerson | null;
  owner: DeskPerson | null;
  thread: ThreadMessage[];
  internal_notes: Array<{ id: string; body: string; classification: string; created_at: string; author: DeskPerson | null; moderation_case_id: string | null }>;
  links: Array<{ id: string; entity_type: string; entity_id: string; link_type: string; label?: string }>;
  customer: {
    joined_at: string | null;
    location: string | null;
    restriction: string | null;
    reports_against: number;
    recent_bookings: Array<{ id: string; public_id: string; status: string; starts_at: string | null; business_name: string | null }>;
    other_tickets: Array<{ id: string; public_id: string; ref: string; status: string; queue: string; contact_reason: string | null; created_at: string | null; csat_rating: number | null }>;
  };
  assignees: DeskPerson[];
}

export interface Macro {
  id: string;
  title: string;
  body: string;
  queue: string | null;
  is_active: boolean;
  use_count: number;
}

const BASE = `${API_VERSION_PREFIX}/hq/support/desk`;

export function createSupportDeskApi(client: ApiClient) {
  return {
    view: (id: string) => client.get<TicketView>(`${BASE}/tickets/${id}`),
    reply: (id: string, body: { body: string; then: "keep" | "pending_customer" | "resolve"; macro_id?: string }) =>
      client.post(`${BASE}/tickets/${id}/reply`, body, newIdempotencyKey()),
    updateFields: (id: string, body: { priority?: string; queue?: string; tags?: string[]; sla_due_at?: string | null }) =>
      client.patch(`${BASE}/tickets/${id}`, body, newIdempotencyKey()),
    merge: (id: string, intoTicketId: string) =>
      client.post(`${BASE}/tickets/${id}/merge`, { into_ticket_id: intoTicketId }, newIdempotencyKey()),
    macros: (includeInactive = false) =>
      client.get<{ macros: Macro[] }>(`${BASE}/macros${includeInactive ? "?include_inactive=true" : ""}`),
    createMacro: (body: { title: string; body: string; queue?: string | null }) =>
      client.post(`${BASE}/macros`, body, newIdempotencyKey()),
    updateMacro: (id: string, body: { title?: string; body?: string; queue?: string | null; is_active?: boolean }) =>
      client.patch(`${BASE}/macros/${id}`, body, newIdempotencyKey()),
  };
}

export type SupportDeskApi = ReturnType<typeof createSupportDeskApi>;

export function renderMacro(body: string, ctx: { name: string | null; ticketRef: string }): string {
  const first = ctx.name?.trim().split(/\s+/)[0] || "there";
  return body.replace(/\{\{\s*name\s*\}\}/gi, first).replace(/\{\{\s*ticket\s*\}\}/gi, ctx.ticketRef);
}
